import { useState, useEffect, useMemo, useRef, useCallback, ChangeEvent } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Toolbar,
  AppBar,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  FormHelperText,
  Grid,
  Collapse,
  Dialog,
  Radio,
  RadioGroup,
  FormControlLabel,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Divider,
  SelectChangeEvent,
  AlertColor,
  ToggleButton,
  ToggleButtonGroup,
  Switch,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import LogoutIcon from '@mui/icons-material/Logout';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EditIcon from '@mui/icons-material/Edit';
import api, { IMAGE_BASE_URL } from '../services/api';
import { getUserData } from '../utils/cookies';
import { decodeMMCParams, encodeMMCParams, hasURLParams, clearURLParams } from '../utils/urlParams';
import {
  PageName,
  ApiResponse,
  Make,
  Model,
  MakeWithModels,
  FlatModel,
  Color,
  Body,
  VehicleType,
  Flag,
  Dataset,
  Vehicle,
  EditField,
  User,
  TaskSummary,
} from '../types';
import { AxiosError } from 'axios';
import {
  DrawerNavigation,
  DRAWER_WIDTH,
  COLLAPSED_WIDTH,
  SendToTaskPanel,
  SelectionHeader,
  BulkEditToolbar,
  ConfirmDialog,
  PaginationBar,
  MMCCard,
} from '../components';
import { deleteVehicles } from '../services/mmcService';
import { TASK_TYPE_CONFIG, getTaskBgColor } from '../constants/taskConfig';

// Year validation constants
const MAX_YEAR = new Date().getFullYear() + 2; // Allow up to 2 years in future for concept cars

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100, 200];
const MAX_COLORS = 3;

// Menu props for dropdowns
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: 300,
    },
  },
};

// Helper function to determine if a color is light
const isLightColor = (hex?: string): boolean => {
  if (!hex) return true;
  const color = hex.replace('#', '');
  const r = parseInt(color.substr(0, 2), 16);
  const g = parseInt(color.substr(2, 2), 16);
  const b = parseInt(color.substr(4, 2), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128;
};

// Snackbar state type
interface SnackbarState {
  open: boolean;
  message: string;
  severity: AlertColor;
}

// Search response type
interface SearchResponse {
  vehicles: Vehicle[];
  items?: Vehicle[];
  totalResults: number;
}

interface MMCSearchProps {
  onLogout: () => void;
  onNavigate: (page: PageName) => void;
}

function MMCSearch({ onLogout, onNavigate }: MMCSearchProps) {
  const theme = useTheme();
  // ============ UI STATES ============
  const [mobileOpen, setMobileOpen] = useState<boolean>(false);
  const [drawerCollapsed, setDrawerCollapsed] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [searching, setSearching] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [advancedOpen, setAdvancedOpen] = useState<boolean>(false);

  // ============ DROPDOWN DATA STATES ============
  const [models, setModels] = useState<Model[]>([]);
  const [makeWithModels, setMakeWithModels] = useState<MakeWithModels[]>([]);
  const [colors, setColors] = useState<Color[]>([]);
  const [bodies, setBodies] = useState<Body[]>([]);
  const [types, setTypes] = useState<VehicleType[]>([]);
  const [flags, setFlags] = useState<Flag[]>([]);
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [tasks, setTasks] = useState<TaskSummary[]>([]);
  const [loadingTasks, setLoadingTasks] = useState<boolean>(true);

  // ============ SEARCH FORM STATES ============
  const [selectedMake, setSelectedMake] = useState<string>('');
  const [selectedModel, setSelectedModel] = useState<string>('');
  const [selectedColors, setSelectedColors] = useState<string[]>([]);
  const [selectedBody, setSelectedBody] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [selectedDatasets, setSelectedDatasets] = useState<string[]>([]);
  const [year, setYear] = useState<string>('');
  const [vin, setVin] = useState<string>('');
  const [tag, setTag] = useState<string>('');
  const [idFilter, setIdFilter] = useState<string>('');

  // Flag filters
  const [flagsMustHave, setFlagsMustHave] = useState<string[]>([]);
  const [flagsAtLeastOne, setFlagsAtLeastOne] = useState<string[]>([]);
  const [flagsDontHave, setFlagsDontHave] = useState<string[]>([]);

  // Color filter mode: 'any' (no filter), 'none' (no colors), 'notEmpty' (has colors), 'specific' (selected colors)
  type ColorFilterMode = 'any' | 'none' | 'notEmpty' | 'specific';
  const [colorFilterMode, setColorFilterMode] = useState<ColorFilterMode>('any');
  const [colorCheckedFilter, setColorCheckedFilter] = useState<'any' | 'checked' | 'unchecked'>('any');

  // Pagination and sorting
  const [pageSize, setPageSize] = useState<number>(20);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sorting, setSorting] = useState<string>('reverse');

  // ============ RESULTS STATES ============
  const [results, setResults] = useState<Vehicle[]>([]);
  const [totalResults, setTotalResults] = useState<number>(0);
  const [hasSearched, setHasSearched] = useState<boolean>(false);

  // ============ SELECTION & EDITING STATES ============
  const [selectedVehicles, setSelectedVehicles] = useState<string[]>([]);
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(new Set());
  const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);
  const [editField, setEditField] = useState<EditField | null>(null);
  const [editValue, setEditValue] = useState<any>(null);
  const [editModelValue, setEditModelValue] = useState<string>('');
  const [editClearBTY, setEditClearBTY] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', severity: 'success' });

  // Delete state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  // Remove box state
  const [removeBoxDialogOpen, setRemoveBoxDialogOpen] = useState<boolean>(false);
  const [removingBox, setRemovingBox] = useState<boolean>(false);

  const config = TASK_TYPE_CONFIG.mmc;
  const IconComponent = config.icon;

  // Calculate total pages
  const totalPages = Math.ceil(totalResults / pageSize) || 0;

  // Memoize selected vehicles Set for O(1) lookups
  const selectedVehiclesSet = useMemo(() => new Set(selectedVehicles), [selectedVehicles]);

  // Filter out hidden items
  const visibleResults = useMemo(() =>
    results.filter(v => !hiddenIds.has(v.id)),
    [results, hiddenIds]
  );

  // Sort makes alphabetically
  const sortedMakes = useMemo(() => {
    return [...makeWithModels].sort((a, b) => a.label.localeCompare(b.label));
  }, [makeWithModels]);

  // Get available models for selected make (sorted alphabetically)
  const availableModels = useMemo((): FlatModel[] => {
    if (!selectedMake) return [];
    const make = makeWithModels.find((m) => m.id === selectedMake);
    const flatModels = make?.models || [];
    return [...flatModels].sort((a, b) => a.label.localeCompare(b.label));
  }, [selectedMake, makeWithModels]);

  // Sort colors alphabetically
  const sortedColors = useMemo(() => {
    return [...colors].sort((a, b) => a.label.localeCompare(b.label));
  }, [colors]);

  // Sort bodies alphabetically
  const sortedBodies = useMemo(() => {
    return [...bodies].sort((a, b) => a.label.localeCompare(b.label));
  }, [bodies]);

  // Sort types alphabetically
  const sortedTypes = useMemo(() => {
    return [...types].sort((a, b) => a.label.localeCompare(b.label));
  }, [types]);

  // Sort flags alphabetically
  const sortedFlags = useMemo(() => {
    return [...flags].sort((a, b) => a.label.localeCompare(b.label));
  }, [flags]);

  // Sort datasets alphabetically
  const sortedDatasets = useMemo(() => {
    return [...datasets].sort((a, b) => a.label.localeCompare(b.label));
  }, [datasets]);

  // Check if user is admin
  const isAdmin = useMemo(() => user?.roles?.includes('admin') || false, [user]);

  // Reset model when make changes (but not when applying URL params)
  const skipModelResetRef = useRef(false);
  useEffect(() => {
    if (skipModelResetRef.current) {
      skipModelResetRef.current = false;
      return;
    }
    setSelectedModel('');
  }, [selectedMake]);

  const lastSelectedRef = useRef<string | null>(null);

  // Ref to prevent double fetching in StrictMode
  const hasFetchedInitialData = useRef(false);

  // Ref to track if URL params have been applied
  const hasAppliedURLParams = useRef(false);

  // Fetch all dropdown data on mount
  useEffect(() => {
    if (hasFetchedInitialData.current) return;
    hasFetchedInitialData.current = true;

    const fetchInitialData = async (): Promise<void> => {
      setLoading(true);
      try {
        // Get user from cookies (stored during login) - no API call needed
        const storedUser = getUserData();
        if (storedUser) {
          setUser({
            id: storedUser.id,
            username: storedUser.username,
            firstName: storedUser.firstName,
            lastName: storedUser.lastName,
            roles: storedUser.roles,
          });
        }

        const [makesRes, modelsRes, colorsRes, bodiesRes, typesRes, flagsRes, datasetsRes, tasksRes] = await Promise.all([
          api.get<ApiResponse<any[]>>('mmc/list/makes'),
          api.get<ApiResponse<any[]>>('mmc/list/models'),
          api.get<ApiResponse<any[]>>('mmc/list/colors'),
          api.get<ApiResponse<any[]>>('mmc/list/bodies'),
          api.get<ApiResponse<any[]>>('mmc/list/types'),
          api.get<ApiResponse<any[]>>('mmc/list/flags'),
          api.get<ApiResponse<any[]>>('datasets/list/mmc'),
          api.get<ApiResponse<TaskSummary[]>>('tasks/list'),
        ]);

        const fetchedMakes = (makesRes.data.results || []) as Make[];
        const fetchedModels = (modelsRes.data.results || []) as Model[];
        const fetchedColors = (colorsRes.data.results || []) as Color[];
        const fetchedBodies = (bodiesRes.data.results || []) as Body[];
        const fetchedTypes = (typesRes.data.results || []) as VehicleType[];
        const fetchedFlags = (flagsRes.data.results || []) as Flag[];
        const fetchedDatasets = (datasetsRes.data.results || []) as Dataset[];

        // Build makeWithModels by grouping models by their serie.make.id
        const makeModelsMap = new Map<string, FlatModel[]>();
        fetchedModels.forEach((model: Model) => {
          const makeId = model.serie?.make?.id;
          if (!makeId) return;
          if (!makeModelsMap.has(makeId)) {
            makeModelsMap.set(makeId, []);
          }
          makeModelsMap.get(makeId)!.push({
            id: model.id,
            label: model.label,
            makeId: makeId,
          });
        });

        const builtMakeWithModels: MakeWithModels[] = fetchedMakes.map((make: Make) => ({
          id: make.id,
          label: make.label,
          models: (makeModelsMap.get(make.id) || []).sort((a, b) => a.label.localeCompare(b.label)),
        }));
        setModels(fetchedModels);
        setMakeWithModels(builtMakeWithModels);
        setColors(fetchedColors);
        setBodies(fetchedBodies);
        setTypes(fetchedTypes);
        setFlags(fetchedFlags);
        setDatasets(fetchedDatasets);

        // Filter tasks to only show mmc type
        const fetchedTasks = (tasksRes.data.results || [])
          .filter((task: TaskSummary) => task.type === 'mmc')
          .sort((a: TaskSummary, b: TaskSummary) => a.title.localeCompare(b.title));
        setTasks(fetchedTasks);
        setLoadingTasks(false);
      } catch (err) {
        console.error('Failed to fetch initial data:', err);
        setError('Failed to load form data. Please refresh the page.');
      } finally {
        setLoading(false);
      }
    };
    fetchInitialData();
  }, []);

  // Apply URL params after initial data is loaded
  useEffect(() => {
    if (loading || hasAppliedURLParams.current || !hasURLParams()) return;
    hasAppliedURLParams.current = true;

    const params = decodeMMCParams();

    // Apply params to state
    // If both make and model are set, skip the model reset that happens when make changes
    if (params.make && params.model) {
      skipModelResetRef.current = true;
    }
    if (params.make) setSelectedMake(params.make);
    if (params.model) setSelectedModel(params.model);
    if (params.colorFilterMode) setColorFilterMode(params.colorFilterMode as ColorFilterMode);
    if (params.colorChecked) setColorCheckedFilter(params.colorChecked as 'any' | 'checked' | 'unchecked');
    if (params.colors && params.colors.length > 0) setSelectedColors(params.colors);
    if (params.body) setSelectedBody(params.body);
    if (params.type) setSelectedType(params.type);
    if (params.datasets && params.datasets.length > 0) setSelectedDatasets(params.datasets);
    if (params.year) setYear(params.year);
    if (params.vin) setVin(params.vin);
    if (params.tag) setTag(params.tag);
    if (params.id) setIdFilter(params.id);
    if (params.mustHave && params.mustHave.length > 0) setFlagsMustHave(params.mustHave);
    if (params.atLeastOne && params.atLeastOne.length > 0) setFlagsAtLeastOne(params.atLeastOne);
    if (params.dontHave && params.dontHave.length > 0) setFlagsDontHave(params.dontHave);
    if (params.sort) setSorting(params.sort);
    if (params.pageSize) setPageSize(params.pageSize);
    if (params.page) setCurrentPage(params.page);

    // Open advanced options if any advanced params are set
    if (params.colors?.length || params.colorFilterMode || params.colorChecked ||
        params.datasets?.length || params.body || params.type ||
        params.year || params.vin || params.tag || params.id ||
        params.mustHave?.length || params.atLeastOne?.length || params.dontHave?.length) {
      setAdvancedOpen(true);
    }

    // Auto-search with URL params
    setTimeout(() => {
      handleSearchRef.current(false);
    }, 0);
  }, [loading]);

  const handleDrawerToggle = (): void => {
    setMobileOpen(!mobileOpen);
  };

  const handleColorChange = (colorIds: string[]): void => {
    // Limit to MAX_COLORS (3)
    if (colorIds.length <= MAX_COLORS) {
      setSelectedColors(colorIds);
    }
  };

  const handleYearChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;
    // Only allow numeric input within valid range
    if (value === '') {
      setYear('');
    } else if (/^\d+$/.test(value)) {
      const numValue = parseInt(value, 10);
      // Only set if within reasonable range (allow partial input like "19" for "1990")
      if (numValue <= MAX_YEAR) {
        setYear(value);
      }
    }
  };

  const handleSearch = async (resetPage = true): Promise<void> => {
    setSearching(true);
    setError('');
    setHiddenIds(new Set());
    setSelectedVehicles([]); lastSelectedRef.current = null;

    const page = resetPage ? 1 : currentPage;
    if (resetPage) {
      setCurrentPage(1);
    }

    try {
      // Build colors filter based on mode
      const colorsFilter = colorFilterMode === 'notEmpty' ? 'notEmpty'
        : colorFilterMode === 'none' ? []
        : colorFilterMode === 'specific' && selectedColors.length > 0 ? selectedColors
        : undefined;

      const payload = {
        ...(selectedMake && { make: selectedMake }),
        ...(selectedModel && { model: selectedModel }),
        ...(colorsFilter !== undefined && { colors: colorsFilter }),
        ...(selectedBody && { body: selectedBody }),
        ...(selectedType && { type: selectedType }),
        ...(selectedDatasets.length > 0 && { datasets: selectedDatasets }),
        ...(year && { year: parseInt(year, 10) }),
        ...(vin && { vin }),
        ...(tag && { tag: tag }),
        ...(idFilter.trim() && { id: idFilter.trim() }),
        ...(colorCheckedFilter === 'checked' && { colorChecked: true }),
        ...(colorCheckedFilter === 'unchecked' && { colorChecked: false }),
        flags: {
          mustHave: flagsMustHave,
          atLeastOne: flagsAtLeastOne,
          dontHave: flagsDontHave,
        },
        sorting: sorting,
        pageSize: pageSize,
        page: page,
      };

      const response = await api.post<ApiResponse<SearchResponse>>('mmc/search', payload);
      const data = response.data.results;

      setResults(data.items || []);
      setTotalResults(data.totalResults || 0);
      setHasSearched(true);

      // Update URL with search params
      encodeMMCParams({
        make: selectedMake || undefined,
        model: selectedModel || undefined,
        colors: selectedColors,
        colorFilterMode: colorFilterMode,
        colorChecked: colorCheckedFilter,
        body: selectedBody || undefined,
        type: selectedType || undefined,
        datasets: selectedDatasets,
        year: year || undefined,
        vin: vin || undefined,
        tag: tag || undefined,
        id: idFilter.trim() || undefined,
        mustHave: flagsMustHave,
        atLeastOne: flagsAtLeastOne,
        dontHave: flagsDontHave,
        sort: sorting,
        pageSize: pageSize,
        page: page,
      });
    } catch (err) {
      const axiosError = err as AxiosError<{ message?: string }>;
      console.error('Search failed:', err);
      setError(axiosError.response?.data?.message || 'Search failed. Please try again.');
      // Don't clear results on error - keep previous results visible
      // User can see what they had while the error is displayed
    } finally {
      setSearching(false);
    }
  };

  const handlePrevious = (): void => {
    if (currentPage > 1) {
      setHiddenIds(new Set());
      setSelectedVehicles([]); lastSelectedRef.current = null;
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handleNext = (): void => {
    if (currentPage < totalPages) {
      setHiddenIds(new Set());
      setSelectedVehicles([]); lastSelectedRef.current = null;
      setCurrentPage((prev) => prev + 1);
    }
  };

  // Re-search when page changes (but only if already searched)
  // Using a ref to store the latest search function to avoid stale closures
  const handleSearchRef = useRef(handleSearch);
  handleSearchRef.current = handleSearch;

  // Track previous page to only trigger search on actual page changes
  const prevPageRef = useRef(currentPage);

  useEffect(() => {
    // Only re-search if page actually changed (not on initial search)
    if (hasSearched && currentPage !== prevPageRef.current) {
      handleSearchRef.current(false);
    }
    prevPageRef.current = currentPage;
  }, [currentPage, hasSearched]);

  const handleReset = (): void => {
    setSelectedMake('');
    setSelectedModel('');
    setSelectedColors([]);
    setColorFilterMode('any');
    setColorCheckedFilter('any');
    setSelectedBody('');
    setSelectedType('');
    setSelectedDatasets([]);
    setYear('');
    setVin('');
    setTag('');
    setIdFilter('');
    setFlagsMustHave([]);
    setFlagsAtLeastOne([]);
    setFlagsDontHave([]);
    setSorting('normal');
    setPageSize(20);
    setCurrentPage(1);
    setResults([]);
    setTotalResults(0);
    setHasSearched(false);
    setError('');
    clearURLParams();
  };

  // Get color display info
  const getColorDisplay = (color: Color): { label: string; hex: string; textColor: string } => {
    return {
      label: color.label,
      hex: color.hex || '#ccc',
      textColor: isLightColor(color.hex) ? '#000' : '#fff',
    };
  };

  // ============ SELECTION & EDITING FUNCTIONS ============
  const handleSelectVehicle = useCallback((vehicleId: string, shiftKey?: boolean): void => {
    if (shiftKey) {
      const anchorId = lastSelectedRef.current || visibleResults[0]?.id;
      if (anchorId) {
        const lastIdx = visibleResults.findIndex(v => v.id === anchorId);
        const currIdx = visibleResults.findIndex(v => v.id === vehicleId);
        if (lastIdx !== -1 && currIdx !== -1) {
          const start = Math.min(lastIdx, currIdx);
          const end = Math.max(lastIdx, currIdx);
          const rangeIds = visibleResults.slice(start, end + 1).map(v => v.id);
          setSelectedVehicles(prev => [...new Set([...prev, ...rangeIds])]);
          lastSelectedRef.current = vehicleId;
          return;
        }
      }
    }
    lastSelectedRef.current = vehicleId;
    setSelectedVehicles((prev) =>
      prev.includes(vehicleId)
        ? prev.filter((id) => id !== vehicleId)
        : [...prev, vehicleId]
    );
  }, [visibleResults]);

  const handleHideItem = useCallback((id: string) => {
    setHiddenIds(prev => new Set(prev).add(id));
  }, []);

  const handleSelectAll = (): void => {
    if (selectedVehicles.length === visibleResults.length) {
      setSelectedVehicles([]); lastSelectedRef.current = null;
    } else {
      setSelectedVehicles(visibleResults.map((v) => v.id));
    }
  };

  const handleInvertSelection = (): void => {
    const currentSet = new Set(selectedVehicles);
    setSelectedVehicles(visibleResults.filter((v) => !currentSet.has(v.id)).map((v) => v.id));
  };

  const handleSelectWithBox = (): void => {
    setSelectedVehicles(visibleResults.filter((v) => v.box && v.box.length > 0).map((v) => v.id));
  };

  const openEditDialog = (vehicle: Vehicle, field: EditField): void => {
    setEditingVehicle(vehicle);
    setEditField(field);
    // Set initial value based on field
    // Note: make is now accessed via vehicle.model?.serie?.make
    switch (field) {
      case 'make':
        setEditValue(vehicle.model?.serie?.make?.id || '');
        setEditModelValue(vehicle.model?.id || '');
        break;
      case 'model':
        setEditValue(vehicle.model?.id || '');
        break;
      case 'colors':
        setEditValue(vehicle.colors?.map((c) => c.id) || []);
        break;
      case 'body':
        setEditValue(vehicle.body?.id || '');
        break;
      case 'type':
        setEditValue(vehicle.type?.id || '');
        break;
      case 'year':
        setEditValue(vehicle.year || '');
        break;
      case 'flags':
        setEditValue(vehicle.flags?.map((f) => f.id) || []);
        break;
      case 'dataset':
        setEditValue(vehicle.dataset?.id || '');
        break;
      default:
        setEditValue(null);
    }
    setEditDialogOpen(true);
  };

  const closeEditDialog = (): void => {
    setEditDialogOpen(false);
    setEditingVehicle(null);
    setEditField(null);
    setEditValue(null);
    setEditModelValue('');
    setEditClearBTY(false);
  };

  // Helper function to build Model structure from model ID
  const buildModelFromId = (modelId: string): Model | null => {
    const model = models.find((m) => m.id === modelId);
    if (!model) return null;
    return model;
  };

  const saveEdit = async (): Promise<void> => {
    if (!editingVehicle || !editField) return;

    setSaving(true);
    try {
      const payload: Record<string, any> = {};

      // Only include the changed field
      // Note: Backend only needs model ID (make is derived from model.serie.make)
      // Backend expects objects with 'id' field - onlyIds() extracts them
      switch (editField) {
        case 'make':
          // When changing make, we also change the model
          payload.model = { id: editModelValue };
          if (editClearBTY) {
            payload.body = null;
            payload.type = null;
            payload.year = null;
          }
          break;
        case 'model':
          if (editValue !== editingVehicle.model?.id) {
            payload.model = { id: editValue };
          }
          break;
        case 'colors':
          payload.colors = (editValue || []).map((id: string) => ({ id }));
          break;
        case 'body':
          if (editValue !== editingVehicle.body?.id) {
            payload.body = { id: editValue };
          }
          break;
        case 'type':
          if (editValue !== editingVehicle.type?.id) {
            payload.type = { id: editValue };
          }
          break;
        case 'year':
          if (editValue !== editingVehicle.year) {
            payload.year = editValue ? parseInt(editValue, 10) : null;
          }
          break;
        case 'flags':
          payload.flags = (editValue || []).map((id: string) => ({ id }));
          break;
        case 'dataset':
          if (editValue !== editingVehicle.dataset?.id) {
            payload.dataset = { id: editValue };
          }
          break;
        case 'colorChecked':
          payload.colorChecked = editValue === true;
          break;
        default:
          break;
      }

      if (Object.keys(payload).length === 0) {
        closeEditDialog();
        return;
      }

      await api.patch(`mmc/patch/${editingVehicle.id}`, payload);

      // Update local state
      // Note: payload fields are now {id: ...} objects, extract the id for lookups
      setResults((prev) =>
        prev.map((v) => {
          if (v.id !== editingVehicle.id) return v;
          const updated = { ...v };
          if (payload.model !== undefined) {
            // Build the full model structure with nested serie.make
            updated.model = buildModelFromId(payload.model.id);
          }
          if (payload.colors) {
            updated.colors = payload.colors.map((c: { id: string }) => {
              const color = colors.find((col) => col.id === c.id);
              return color || { id: c.id, label: c.id };
            });
          }
          if (payload.body !== undefined) {
            if (payload.body === null) {
              updated.body = null;
            } else {
              const body = bodies.find((b) => b.id === payload.body.id);
              updated.body = body ? { id: body.id, label: body.label } : v.body;
            }
          }
          if (payload.type !== undefined) {
            if (payload.type === null) {
              updated.type = null;
            } else {
              const type = types.find((t) => t.id === payload.type.id);
              updated.type = type ? { id: type.id, label: type.label } : v.type;
            }
          }
          if (payload.year !== undefined) {
            updated.year = payload.year;
          }
          if (payload.flags) {
            updated.flags = payload.flags.map((f: { id: string }) => {
              const flag = flags.find((fl) => fl.id === f.id);
              return flag || { id: f.id, label: f.id };
            });
          }
          if (payload.dataset) {
            const dataset = datasets.find((d) => d.id === payload.dataset.id);
            updated.dataset = dataset ? { id: dataset.id, label: dataset.label } : v.dataset;
          }
          if (payload.colorChecked !== undefined) {
            updated.colorChecked = payload.colorChecked;
          }
          return updated;
        })
      );

      setSnackbar({ open: true, message: 'Vehicle updated successfully', severity: 'success' });
      closeEditDialog();
    } catch (err) {
      const axiosError = err as AxiosError<{ message?: string }>;
      console.error('Failed to save:', err);
      setSnackbar({ open: true, message: axiosError.response?.data?.message || 'Failed to save changes', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Direct toggle for colorChecked (no dialog needed for individual items)
  const handleToggleColorChecked = async (vehicle: Vehicle): Promise<void> => {
    const newValue = !vehicle.colorChecked;
    try {
      await api.patch(`mmc/patch/${vehicle.id}`, { colorChecked: newValue });
      setResults((prev) =>
        prev.map((v) => (v.id === vehicle.id ? { ...v, colorChecked: newValue } : v))
      );
      setSnackbar({
        open: true,
        message: `Color ${newValue ? 'verified' : 'unverified'}`,
        severity: 'success',
      });
    } catch (err) {
      console.error('Failed to toggle colorChecked:', err);
      setSnackbar({ open: true, message: 'Failed to update', severity: 'error' });
    }
  };

  const saveBulkEdit = async (): Promise<void> => {
    if (selectedVehicles.length === 0 || !editField) return;

    setSaving(true);
    try {
      const payload: Record<string, any> = {};

      // Note: Backend only needs model ID (make is derived from model.serie.make)
      // Backend expects objects with 'id' field - onlyIds() extracts them
      switch (editField) {
        case 'make':
          // When changing make, we only send the model
          payload.model = { id: editModelValue };
          if (editClearBTY) {
            payload.body = null;
            payload.type = null;
            payload.year = null;
          }
          break;
        case 'model':
          payload.model = { id: editValue };
          break;
        case 'colors':
          payload.colors = (editValue || []).map((id: string) => ({ id }));
          break;
        case 'body':
          payload.body = editValue ? { id: editValue } : null;
          break;
        case 'type':
          payload.type = editValue ? { id: editValue } : null;
          break;
        case 'year':
          payload.year = editValue ? parseInt(editValue, 10) : null;
          break;
        case 'flags':
          payload.flags = (editValue || []).map((id: string) => ({ id }));
          break;
        case 'dataset':
          payload.dataset = { id: editValue };
          break;
        case 'colorChecked':
          payload.colorChecked = editValue === true;
          break;
        case 'vin':
          payload.vin = editValue?.trim() || null;
          break;
        case 'clearBodyTypeYear':
          payload.body = null;
          payload.type = null;
          payload.year = null;
          break;
        default:
          break;
      }

      // Send PATCH for each selected vehicle
      await Promise.all(
        selectedVehicles.map((vehicleId) =>
          api.patch(`mmc/patch/${vehicleId}`, payload)
        )
      );

      // Update local state for all selected vehicles
      // Note: payload fields are now {id: ...} objects, extract the id for lookups
      setResults((prev) =>
        prev.map((v) => {
          if (!selectedVehiclesSet.has(v.id)) return v;
          const updated = { ...v };
          if (payload.model !== undefined) {
            // Build the full model structure with nested serie.make
            updated.model = buildModelFromId(payload.model.id);
          }
          if (payload.colors) {
            updated.colors = payload.colors.map((c: { id: string }) => {
              const color = colors.find((col) => col.id === c.id);
              return color || { id: c.id, label: c.id };
            });
          }
          if (payload.body !== undefined) {
            if (payload.body === null) {
              updated.body = null;
            } else {
              const body = bodies.find((b) => b.id === payload.body.id);
              updated.body = body ? { id: body.id, label: body.label } : v.body;
            }
          }
          if (payload.type !== undefined) {
            if (payload.type === null) {
              updated.type = null;
            } else {
              const type = types.find((t) => t.id === payload.type.id);
              updated.type = type ? { id: type.id, label: type.label } : v.type;
            }
          }
          if (payload.year !== undefined) {
            updated.year = payload.year;
          }
          if (payload.flags) {
            updated.flags = payload.flags.map((f: { id: string }) => {
              const flag = flags.find((fl) => fl.id === f.id);
              return flag || { id: f.id, label: f.id };
            });
          }
          if (payload.dataset) {
            const dataset = datasets.find((d) => d.id === payload.dataset.id);
            updated.dataset = dataset ? { id: dataset.id, label: dataset.label } : v.dataset;
          }
          if (payload.colorChecked !== undefined) {
            updated.colorChecked = payload.colorChecked;
          }
          if (payload.vin !== undefined) {
            updated.vin = payload.vin || undefined;
          }
          return updated;
        })
      );

      setSnackbar({ open: true, message: `${selectedVehicles.length} vehicles updated`, severity: 'success' });
      closeEditDialog();
    } catch (err) {
      const axiosError = err as AxiosError<{ message?: string }>;
      console.error('Failed to save:', err);
      setSnackbar({ open: true, message: axiosError.response?.data?.message || 'Failed to save changes', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const openBulkEditDialog = (field: EditField): void => {
    setEditingVehicle(null);
    setEditField(field);
    // Set initial value
    switch (field) {
      case 'make':
        setEditValue('');
        setEditModelValue('');
        break;
      case 'model':
      case 'body':
      case 'type':
      case 'dataset':
        setEditValue('');
        break;
      case 'colors':
      case 'flags':
        setEditValue([]);
        break;
      case 'year':
        setEditValue('');
        break;
      case 'colorChecked':
        setEditValue(false);
        break;
      case 'vin':
        setEditValue('');
        break;
      case 'clearBodyTypeYear':
        setEditValue(null);
        break;
      default:
        setEditValue(null);
    }
    setEditDialogOpen(true);
  };

  // Handle box update from MMCVehicleCanvas
  const handleBoxUpdate = useCallback(async (vehicleId: string, newBoxArray: number[] | null): Promise<void> => {
    try {
      await api.patch(`mmc/patch/${vehicleId}`, { box: newBoxArray });

      // Update local state
      setResults((prev) =>
        prev.map((v) => {
          if (v.id !== vehicleId) return v;
          return { ...v, box: newBoxArray || undefined };
        })
      );
    } catch (err) {
      const axiosError = err as AxiosError<{ message?: string }>;
      console.error('Failed to update box:', err);
      setSnackbar({ open: true, message: axiosError.response?.data?.message || 'Failed to update box', severity: 'error' });
    }
  }, []);

  // Copy vehicle ID to clipboard
  const handleCopyId = useCallback(async (id: string) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(id);
      } else {
        // Fallback for non-HTTPS contexts
        const textArea = document.createElement('textarea');
        textArea.value = id;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setSnackbar({ open: true, message: 'ID copied to clipboard', severity: 'success' });
    } catch (err) {
      console.error('Failed to copy:', err);
      setSnackbar({ open: true, message: 'Failed to copy ID', severity: 'error' });
    }
  }, []);

  // Get models for edit dialog based on the selected make
  const editAvailableModels = useMemo((): FlatModel[] => {
    if (editField === 'make' && editValue) {
      const make = makeWithModels.find((m) => m.id === editValue);
      const flatModels = make?.models || [];
      return [...flatModels].sort((a, b) => a.label.localeCompare(b.label));
    }
    if (editField === 'model' && editingVehicle) {
      // Get make from vehicle.model.serie.make
      const makeId = editingVehicle.model?.serie?.make?.id;
      const make = makeWithModels.find((m) => m.id === makeId);
      const flatModels = make?.models || [];
      return [...flatModels].sort((a, b) => a.label.localeCompare(b.label));
    }
    return [];
  }, [editField, editingVehicle, editValue, makeWithModels]);

  // Bulk delete handler - opens confirmation dialog
  const handleBulkDeleteClick = useCallback(() => {
    setDeleteDialogOpen(true);
  }, []);

  // Confirm delete handler - executes bulk delete
  const handleConfirmDelete = useCallback(async () => {
    setDeleting(true);
    try {
      const { succeeded, failed } = await deleteVehicles(selectedVehicles);
      if (succeeded.length > 0) {
        const succeededSet = new Set(succeeded);
        setResults((prev) => prev.filter((v) => !succeededSet.has(v.id)));
        setTotalResults((prev) => prev - succeeded.length);
        setSelectedVehicles((prev) => prev.filter((id) => !succeededSet.has(id)));
      }
      if (failed.length > 0) {
        setSnackbar({
          open: true,
          message: `Deleted ${succeeded.length}, failed ${failed.length}`,
          severity: 'warning',
        });
      } else {
        setSnackbar({
          open: true,
          message: `Deleted ${succeeded.length} vehicle(s)`,
          severity: 'success',
        });
      }
    } catch (err) {
      console.error('Delete failed:', err);
      setSnackbar({ open: true, message: 'Delete failed', severity: 'error' });
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  }, [selectedVehicles]);

  // Single delete handler - opens confirmation dialog for one vehicle
  const handleDeleteVehicle = useCallback((vehicleId: string) => {
    setDeleteTargetId(vehicleId);
  }, []);

  // Confirm single delete
  const handleConfirmDeleteSingle = useCallback(async () => {
    if (!deleteTargetId) return;
    setDeleting(true);
    try {
      const { succeeded, failed } = await deleteVehicles([deleteTargetId]);
      if (succeeded.length > 0) {
        setResults((prev) => prev.filter((v) => v.id !== deleteTargetId));
        setTotalResults((prev) => prev - 1);
        setSelectedVehicles((prev) => prev.filter((id) => id !== deleteTargetId));
      }
      if (failed.length > 0) {
        setSnackbar({ open: true, message: 'Delete failed', severity: 'error' });
      } else {
        setSnackbar({ open: true, message: 'Vehicle deleted', severity: 'success' });
      }
    } catch (err) {
      console.error('Delete failed:', err);
      setSnackbar({ open: true, message: 'Delete failed', severity: 'error' });
    } finally {
      setDeleting(false);
      setDeleteTargetId(null);
    }
  }, [deleteTargetId]);

  // Bulk remove box handler - opens confirmation dialog
  const handleBulkRemoveBoxClick = useCallback(() => {
    setRemoveBoxDialogOpen(true);
  }, []);

  // Confirm remove box handler - executes bulk remove
  const handleConfirmRemoveBox = useCallback(async () => {
    setRemovingBox(true);
    try {
      await Promise.all(
        selectedVehicles.map((vehicleId) =>
          api.patch(`mmc/patch/${vehicleId}`, { box: null })
        )
      );

      // Update local state - set box to undefined for all selected
      setResults((prev) =>
        prev.map((v) =>
          selectedVehiclesSet.has(v.id) ? { ...v, box: undefined } : v
        )
      );

      setSnackbar({
        open: true,
        message: `Removed box from ${selectedVehicles.length} vehicle(s)`,
        severity: 'success',
      });
    } catch (err) {
      console.error('Remove box failed:', err);
      setSnackbar({ open: true, message: 'Failed to remove box', severity: 'error' });
    } finally {
      setRemovingBox(false);
      setRemoveBoxDialogOpen(false);
    }
  }, [selectedVehicles, selectedVehiclesSet]);

  // Handle page change from PaginationBar
  const handlePageChange = useCallback((page: number) => {
    setHiddenIds(new Set());
    setSelectedVehicles([]); lastSelectedRef.current = null;
    setCurrentPage(page);
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  // Calculate current drawer width based on collapsed state
  const currentDrawerWidth = drawerCollapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { md: `${currentDrawerWidth}px` },
          bgcolor: config.color,
          transition: 'width 0.2s, margin-left 0.2s',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
            <IconComponent size={28} />
            <Typography variant="h6" noWrap component="div">
              {config.label} Search
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {user && (
              <Box
                sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1, mr: 2 }}
              >
                <Typography variant="body2">@{user.username}</Typography>
                {user.roles?.map((role) => (
                  <Chip
                    key={role}
                    label={role}
                    size="small"
                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                  />
                ))}
              </Box>
            )}
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              startIcon={<LogoutIcon />}
              onClick={onLogout}
            >
              Logout
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Side Navigation Drawer */}
      <DrawerNavigation
        currentPage="searchMmc"
        onNavigate={onNavigate}
        collapsed={drawerCollapsed}
        onToggleCollapse={() => setDrawerCollapsed(!drawerCollapsed)}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { md: `${currentDrawerWidth}px` },
          bgcolor: 'background.default',
          transition: 'width 0.2s, margin-left 0.2s',
        }}
      >
        <Toolbar /> {/* Spacer for AppBar */}
        <Container maxWidth="lg" disableGutters>
          {/* Search Header */}
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: getTaskBgColor('mmc', theme.palette.mode),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <IconComponent size={40} />
              </Box>
              <Box>
                <Typography variant="h5" component="h1">
                  Search {config.label}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Search and edit samples in the {config.label.toLowerCase()} dataset
                </Typography>
              </Box>
            </Box>

            {/* Search Form */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Row 1: Make and Model */}
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel id="make-label">Make</InputLabel>
                    <Select
                      labelId="make-label"
                      value={selectedMake}
                      onChange={(e: SelectChangeEvent) => setSelectedMake(e.target.value)}
                      input={<OutlinedInput label="Make" />}
                      MenuProps={MenuProps}
                    >
                      <MenuItem value="">
                        <em>Any</em>
                      </MenuItem>
                      {sortedMakes.map((make) => (
                        <MenuItem key={make.id} value={make.id}>
                          {make.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth disabled={!selectedMake}>
                    <InputLabel id="model-label">Model</InputLabel>
                    <Select
                      labelId="model-label"
                      value={selectedModel}
                      onChange={(e: SelectChangeEvent) => setSelectedModel(e.target.value)}
                      input={<OutlinedInput label="Model" />}
                      MenuProps={MenuProps}
                    >
                      <MenuItem value="">
                        <em>Any</em>
                      </MenuItem>
                      {availableModels.map((model) => (
                        <MenuItem key={model.id} value={model.id}>
                          {model.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {!selectedMake && (
                      <FormHelperText>Select a make first</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              </Grid>

              {/* Advanced Options Toggle */}
              <Button
                variant="text"
                onClick={() => setAdvancedOpen(!advancedOpen)}
                endIcon={advancedOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ alignSelf: 'flex-start' }}
              >
                Advanced Options
              </Button>

              {/* Collapsible Advanced Options */}
              <Collapse in={advancedOpen}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

                {/* Color Filter and Dataset */}
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Color Filter
                      </Typography>
                      <ToggleButtonGroup
                        value={colorFilterMode}
                        exclusive
                        onChange={(_, value) => {
                          if (value !== null) {
                            setColorFilterMode(value);
                            // Clear selected colors when switching away from 'specific'
                            if (value !== 'specific') {
                              setSelectedColors([]);
                            }
                          }
                        }}
                        size="small"
                        sx={{ mb: 1 }}
                      >
                        <ToggleButton value="any">Any</ToggleButton>
                        <ToggleButton value="none">No Colors</ToggleButton>
                        <ToggleButton value="notEmpty">Has Colors</ToggleButton>
                        <ToggleButton value="specific">Specific</ToggleButton>
                      </ToggleButtonGroup>

                      {/* Color grid - only shown when 'specific' mode */}
                      {colorFilterMode === 'specific' && (
                        <>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            Select colors (max 3){selectedColors.length > 0 && ` - ${selectedColors.length} selected`}
                          </Typography>
                          <Paper variant="outlined" sx={{ p: 1, maxHeight: 140, overflow: 'auto' }}>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {sortedColors.map((color) => {
                                const display = getColorDisplay(color);
                                const isSelected = selectedColors.includes(color.id);
                                const isDisabled = selectedColors.length >= MAX_COLORS && !isSelected;
                                return (
                                  <Box
                                    key={color.id}
                                    onClick={() => !isDisabled && handleColorChange(isSelected ? selectedColors.filter(c => c !== color.id) : [...selectedColors, color.id])}
                                    sx={{
                                      width: 36,
                                      height: 36,
                                      bgcolor: display.hex,
                                      borderRadius: 0.5,
                                      border: isSelected ? 2 : 1, borderColor: isSelected ? 'primary.main' : 'divider',
                                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                                      opacity: isDisabled ? 0.4 : 1,
                                      transition: 'all 0.1s',
                                      '&:hover': !isDisabled ? { transform: 'scale(1.1)', zIndex: 1 } : {},
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                    title={display.label}
                                  >
                                    {isSelected && (
                                      <Box sx={{ color: display.textColor, fontSize: 16, fontWeight: 'bold' }}>✓</Box>
                                    )}
                                  </Box>
                                );
                              })}
                            </Box>
                          </Paper>
                        </>
                      )}

                      {/* Color Checked Filter */}
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Color Verified
                        </Typography>
                        <ToggleButtonGroup
                          value={colorCheckedFilter}
                          exclusive
                          onChange={(_, value) => value !== null && setColorCheckedFilter(value)}
                          size="small"
                        >
                          <ToggleButton value="any">Any</ToggleButton>
                          <ToggleButton value="checked">Verified</ToggleButton>
                          <ToggleButton value="unchecked">Not Verified</ToggleButton>
                        </ToggleButtonGroup>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Filter by specific datasets (leave empty for all)
                    </Typography>
                    <FormControl fullWidth>
                      <InputLabel id="dataset-label">Datasets</InputLabel>
                      <Select<string[]>
                        labelId="dataset-label"
                        multiple
                        value={selectedDatasets}
                        onChange={(e) => setSelectedDatasets(e.target.value as string[])}
                        input={<OutlinedInput label="Datasets" />}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((datasetId) => {
                              const dataset = datasets.find((d) => d.id === datasetId);
                              return (
                                <Chip
                                  key={datasetId}
                                  label={dataset?.label || datasetId}
                                  size="small"
                                />
                              );
                            })}
                          </Box>
                        )}
                        MenuProps={MenuProps}
                      >
                        {sortedDatasets.map((dataset) => (
                          <MenuItem key={dataset.id} value={dataset.id}>
                            {dataset.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                  {/* Body Style and Type Style */}
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormControl fullWidth>
                        <InputLabel id="body-label">Body Style</InputLabel>
                        <Select
                          labelId="body-label"
                          value={selectedBody}
                          onChange={(e: SelectChangeEvent) => setSelectedBody(e.target.value)}
                          input={<OutlinedInput label="Body Style" />}
                          MenuProps={MenuProps}
                        >
                          <MenuItem value="">
                            <em>Any</em>
                          </MenuItem>
                          {sortedBodies.map((body) => (
                            <MenuItem key={body.id} value={body.id}>
                              {body.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormControl fullWidth>
                        <InputLabel id="type-label">Type Style</InputLabel>
                        <Select
                          labelId="type-label"
                          value={selectedType}
                          onChange={(e: SelectChangeEvent) => setSelectedType(e.target.value)}
                          input={<OutlinedInput label="Type Style" />}
                          MenuProps={MenuProps}
                        >
                          <MenuItem value="">
                            <em>Any</em>
                          </MenuItem>
                          {sortedTypes.map((type) => (
                            <MenuItem key={type.id} value={type.id}>
                              {type.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>

                  {/* Year, VIN, Tag, ID */}
                  <Grid container spacing={2} alignItems="center">
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <TextField
                        fullWidth
                        label="Year"
                        value={year}
                        onChange={handleYearChange}
                        placeholder="e.g., 2020"
                        inputProps={{ inputMode: 'numeric' }}
                      />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <TextField
                        fullWidth
                        label="VIN"
                        value={vin}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setVin(e.target.value)}
                        placeholder="VIN"
                      />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <TextField
                        fullWidth
                        label="Tag"
                        value={tag}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setTag(e.target.value)}
                        placeholder="License plate"
                      />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <TextField
                        fullWidth
                        label="ID"
                        value={idFilter}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setIdFilter(e.target.value)}
                        placeholder="MongoDB ObjectId"
                      />
                    </Grid>
                    </Grid>

                  {/* Flag Filters */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Flag Filters
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Filter samples based on their flags. Leave empty to include all samples.
                    </Typography>

                    {sortedFlags.length === 0 ? (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        No flags available for MMC type.
                      </Alert>
                    ) : (
                      <Grid container spacing={2}>
                        {/* Must Have Flags */}
                        <Grid size={{ xs: 12, md: 4 }}>
                          <FormControl fullWidth size="small">
                            <InputLabel id="flags-must-have-label">Must Have</InputLabel>
                            <Select<string[]>
                              labelId="flags-must-have-label"
                              multiple
                              value={flagsMustHave}
                              onChange={(e) => setFlagsMustHave(e.target.value as string[])}
                              input={<OutlinedInput label="Must Have" />}
                              renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {selected.map((flagId) => {
                                    const flag = flags.find((f) => f.id === flagId);
                                    return (
                                      <Chip
                                        key={flagId}
                                        label={flag?.label || flagId}
                                        size="small"
                                        color="success"
                                      />
                                    );
                                  })}
                                </Box>
                              )}
                              MenuProps={MenuProps}
                            >
                              {sortedFlags.map((flag) => (
                                <MenuItem
                                  key={flag.id}
                                  value={flag.id}
                                  disabled={
                                    flagsAtLeastOne.includes(flag.id) || flagsDontHave.includes(flag.id)
                                  }
                                >
                                  {flag.label}
                                </MenuItem>
                              ))}
                            </Select>
                            <FormHelperText>Must have ALL</FormHelperText>
                          </FormControl>
                        </Grid>

                        {/* At Least One Flags */}
                        <Grid size={{ xs: 12, md: 4 }}>
                          <FormControl fullWidth size="small">
                            <InputLabel id="flags-at-least-one-label">At Least One</InputLabel>
                            <Select<string[]>
                              labelId="flags-at-least-one-label"
                              multiple
                              value={flagsAtLeastOne}
                              onChange={(e) => setFlagsAtLeastOne(e.target.value as string[])}
                              input={<OutlinedInput label="At Least One" />}
                              renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {selected.map((flagId) => {
                                    const flag = flags.find((f) => f.id === flagId);
                                    return (
                                      <Chip
                                        key={flagId}
                                        label={flag?.label || flagId}
                                        size="small"
                                        color="info"
                                      />
                                    );
                                  })}
                                </Box>
                              )}
                              MenuProps={MenuProps}
                            >
                              {sortedFlags.map((flag) => (
                                <MenuItem
                                  key={flag.id}
                                  value={flag.id}
                                  disabled={
                                    flagsMustHave.includes(flag.id) || flagsDontHave.includes(flag.id)
                                  }
                                >
                                  {flag.label}
                                </MenuItem>
                              ))}
                            </Select>
                            <FormHelperText>At least ONE</FormHelperText>
                          </FormControl>
                        </Grid>

                        {/* Don't Have Flags */}
                        <Grid size={{ xs: 12, md: 4 }}>
                          <FormControl fullWidth size="small">
                            <InputLabel id="flags-dont-have-label">Don't Have</InputLabel>
                            <Select<string[]>
                              labelId="flags-dont-have-label"
                              multiple
                              value={flagsDontHave}
                              onChange={(e) => setFlagsDontHave(e.target.value as string[])}
                              input={<OutlinedInput label="Don't Have" />}
                              renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {selected.map((flagId) => {
                                    const flag = flags.find((f) => f.id === flagId);
                                    return (
                                      <Chip
                                        key={flagId}
                                        label={flag?.label || flagId}
                                        size="small"
                                        color="error"
                                      />
                                    );
                                  })}
                                </Box>
                              )}
                              MenuProps={MenuProps}
                            >
                              {sortedFlags.map((flag) => (
                                <MenuItem
                                  key={flag.id}
                                  value={flag.id}
                                  disabled={
                                    flagsMustHave.includes(flag.id) || flagsAtLeastOne.includes(flag.id)
                                  }
                                >
                                  {flag.label}
                                </MenuItem>
                              ))}
                            </Select>
                            <FormHelperText>Must NOT have</FormHelperText>
                          </FormControl>
                        </Grid>
                      </Grid>
                    )}
                  </Box>
                </Box>
              </Collapse>

              {/* Page Size, Sorting and Buttons */}
              <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="page-size-label">Page Size</InputLabel>
                    <Select
                      labelId="page-size-label"
                      value={pageSize}
                      onChange={(e: SelectChangeEvent<number>) => setPageSize(e.target.value as number)}
                      label="Page Size"
                    >
                      {PAGE_SIZE_OPTIONS.map((size) => (
                        <MenuItem key={size} value={size}>
                          {size}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 8, md: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2">Sorting:</Typography>
                    <RadioGroup
                      row
                      value={sorting}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => setSorting(e.target.value)}
                    >
                      <FormControlLabel value="normal" control={<Radio size="small" />} label="Normal" />
                      <FormControlLabel value="reverse" control={<Radio size="small" />} label="Reverse" />
                      <FormControlLabel value="random" control={<Radio size="small" />} label="Random" />
                    </RadioGroup>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button variant="outlined" onClick={handleReset} disabled={searching}>
                      Reset
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={searching ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                      onClick={() => handleSearch(true)}
                      disabled={searching}
                      sx={{ bgcolor: config.color }}
                    >
                      {searching ? 'Searching...' : 'Search'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* Results Section */}
          {hasSearched && (
            <Paper elevation={1} sx={{ p: 3 }}>
              {results.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="h6" color="text.secondary">
                    No results found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Try adjusting your search criteria
                  </Typography>
                </Box>
              ) : (
                <>
                  {/* Pagination Header */}
                  <PaginationBar
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalResults={totalResults}
                    disabled={searching}
                    onPrevious={handlePrevious}
                    onNext={handleNext}
                    onPageChange={handlePageChange}
                  />

                  {/* Selection Header */}
                  <SelectionHeader
                    selectedCount={selectedVehicles.length}
                    totalCount={visibleResults.length}
                    onToggleSelectAll={handleSelectAll}
                    itemLabel="vehicles"
                    rightContent={
                      visibleResults.length > 0 ? (
                        <>
                          <Button size="small" onClick={handleSelectWithBox}>
                            Select With Box
                          </Button>
                          <Button size="small" onClick={handleInvertSelection}>
                            Invert Selection
                          </Button>
                        </>
                      ) : undefined
                    }
                  />

                  {/* Bulk Edit Toolbar */}
                  <BulkEditToolbar
                    visible={selectedVehicles.length > 0}
                    actions={[
                      { key: 'make', label: 'Make & Model', onClick: () => openBulkEditDialog('make') },
                      { key: 'colors', label: 'Colors', onClick: () => openBulkEditDialog('colors') },
                      { key: 'colorChecked', label: 'Color Verified', onClick: () => openBulkEditDialog('colorChecked') },
                      { key: 'body', label: 'Body', onClick: () => openBulkEditDialog('body') },
                      { key: 'type', label: 'Type', onClick: () => openBulkEditDialog('type') },
                      { key: 'year', label: 'Year', onClick: () => openBulkEditDialog('year') },
                      { key: 'clearBodyTypeYear', label: 'Clear Body/Type/Year', onClick: () => openBulkEditDialog('clearBodyTypeYear') },
                      { key: 'vin', label: 'VIN', onClick: () => openBulkEditDialog('vin') },
                      { key: 'flags', label: 'Flags', onClick: () => openBulkEditDialog('flags') },
                      { key: 'dataset', label: 'Dataset', onClick: () => openBulkEditDialog('dataset') },
                      { key: 'removeBox', label: 'Remove Box', onClick: handleBulkRemoveBoxClick },
                      ...(isAdmin ? [{ key: 'delete', label: 'Delete', onClick: handleBulkDeleteClick }] : []),
                    ]}
                    onClearSelection={() => { setSelectedVehicles([]); lastSelectedRef.current = null; }}
                    bgcolor={getTaskBgColor('mmc', theme.palette.mode)}
                  />

                  {/* Send to Task Panel */}
                  <Paper sx={{ p: 1.5, mb: 2, bgcolor: getTaskBgColor('mmc', theme.palette.mode) }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2" sx={{ mr: 1 }}>Send to task:</Typography>
                      <SendToTaskPanel
                        tasks={tasks}
                        loadingTasks={loadingTasks}
                        selectedIds={selectedVehicles}
                        allResultIds={results.map((v) => v.id)}
                        themeColor="#1976d2"
                        onSuccess={(count) => setSnackbar({ open: true, message: `Sent ${count} sample(s) to task`, severity: 'success' })}
                        onError={(msg) => setSnackbar({ open: true, message: msg, severity: 'error' })}
                      />
                    </Box>
                  </Paper>

                  <Divider sx={{ mb: 2 }} />

                  {/* Results Grid */}
                  <Grid container spacing={2}>
                    {visibleResults.map((vehicle) => (
                      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={vehicle.id}>
                        <MMCCard
                          vehicle={vehicle}
                          imageBaseUrl={IMAGE_BASE_URL}
                          isSelected={selectedVehiclesSet.has(vehicle.id)}
                          onToggleSelection={handleSelectVehicle}
                          onBoxUpdate={handleBoxUpdate}
                          onEditField={openEditDialog}
                          onHide={handleHideItem}
                          onCopyId={handleCopyId}
                          onToggleColorChecked={handleToggleColorChecked}
                          onDelete={isAdmin ? handleDeleteVehicle : undefined}
                        />
                      </Grid>
                    ))}
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  {/* Pagination Footer */}
                  <PaginationBar
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalResults={totalResults}
                    disabled={searching}
                    onPrevious={handlePrevious}
                    onNext={handleNext}
                    onPageChange={handlePageChange}
                  />
                </>
              )}
            </Paper>
          )}

          {/* Initial State - No search yet */}
          {!hasSearched && !error && (
            <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
              <Box
                sx={{
                  p: 3,
                  borderRadius: 2,
                  bgcolor: getTaskBgColor('mmc', theme.palette.mode),
                  display: 'inline-flex',
                  mb: 2,
                }}
              >
                <IconComponent size={64} />
              </Box>
              <Typography variant="h6" gutterBottom>
                Search for MMC Samples
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Use the filters above to search for vehicles by make, model, color, and other
                attributes. Click the Search button to view results.
              </Typography>
            </Paper>
          )}
        </Container>
      </Box>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={closeEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingVehicle
            ? `Edit ${editField === 'make' ? 'Make & Model' : editField ? editField.charAt(0).toUpperCase() + editField.slice(1) : ''}`
            : `Bulk Edit ${editField === 'make' ? 'Make & Model' : editField ? editField.charAt(0).toUpperCase() + editField.slice(1) : ''} (${selectedVehicles.length} items)`}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {/* Make Field - includes Model selection */}
            {editField === 'make' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel id="edit-make-label">Make</InputLabel>
                  <Select
                    labelId="edit-make-label"
                    value={editValue || ''}
                    onChange={(e: SelectChangeEvent) => {
                      setEditValue(e.target.value);
                      setEditModelValue('');
                    }}
                    input={<OutlinedInput label="Make" />}
                    MenuProps={MenuProps}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {sortedMakes.map((make) => (
                      <MenuItem key={make.id} value={make.id}>
                        {make.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth disabled={!editValue} error={!!editValue && !editModelValue}>
                  <InputLabel id="edit-make-model-label">Model</InputLabel>
                  <Select
                    labelId="edit-make-model-label"
                    value={editModelValue || ''}
                    onChange={(e: SelectChangeEvent) => setEditModelValue(e.target.value)}
                    input={<OutlinedInput label="Model" />}
                    MenuProps={MenuProps}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {editAvailableModels.map((model) => (
                      <MenuItem key={model.id} value={model.id}>
                        {model.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {!editValue ? 'Select a make first' : (editValue && !editModelValue ? 'Please select a model' : '')}
                  </FormHelperText>
                </FormControl>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editClearBTY}
                      onChange={(e) => setEditClearBTY(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Also clear Body, Type & Year"
                />
              </Box>
            )}

            {/* Model Field */}
            {editField === 'model' && (
              <FormControl fullWidth disabled={editAvailableModels.length === 0}>
                <InputLabel id="edit-model-label">Model</InputLabel>
                <Select
                  labelId="edit-model-label"
                  value={editValue || ''}
                  onChange={(e: SelectChangeEvent) => setEditValue(e.target.value)}
                  input={<OutlinedInput label="Model" />}
                  MenuProps={MenuProps}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {editAvailableModels.map((model) => (
                    <MenuItem key={model.id} value={model.id}>
                      {model.label}
                    </MenuItem>
                  ))}
                </Select>
                {editAvailableModels.length === 0 && (
                  <FormHelperText>
                    {editingVehicle ? 'No make assigned to this vehicle' : 'Select a make first for each vehicle'}
                  </FormHelperText>
                )}
              </FormControl>
            )}

            {/* Colors Field */}
            {editField === 'colors' && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Select up to 3 colors{(editValue?.length || 0) > 0 && ` (${editValue.length} selected)`}
                </Typography>
                <Paper variant="outlined" sx={{ p: 1, maxHeight: 180, overflow: 'auto' }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {sortedColors.map((color) => {
                      const display = getColorDisplay(color);
                      const isSelected = (editValue || []).includes(color.id);
                      const isDisabled = (editValue?.length || 0) >= MAX_COLORS && !isSelected;
                      return (
                        <Box
                          key={color.id}
                          onClick={() => {
                            if (isDisabled) return;
                            if (isSelected) {
                              setEditValue((editValue || []).filter((c: string) => c !== color.id));
                            } else {
                              setEditValue([...(editValue || []), color.id]);
                            }
                          }}
                          sx={{
                            width: 40,
                            height: 40,
                            bgcolor: display.hex,
                            borderRadius: 0.5,
                            border: isSelected ? 2 : 1, borderColor: isSelected ? 'primary.main' : 'divider',
                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                            opacity: isDisabled ? 0.4 : 1,
                            transition: 'all 0.1s',
                            '&:hover': !isDisabled ? { transform: 'scale(1.1)', boxShadow: 1 } : {},
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                          title={display.label}
                        >
                          {isSelected && (
                            <Box sx={{ color: display.textColor, fontSize: 18, fontWeight: 'bold' }}>✓</Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Paper>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Leave empty for no colors
                </Typography>
              </Box>
            )}

            {/* Body Field */}
            {editField === 'body' && (
              <FormControl fullWidth>
                <InputLabel id="edit-body-label">Body Style</InputLabel>
                <Select
                  labelId="edit-body-label"
                  value={editValue || ''}
                  onChange={(e: SelectChangeEvent) => setEditValue(e.target.value)}
                  input={<OutlinedInput label="Body Style" />}
                  MenuProps={MenuProps}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {sortedBodies.map((body) => (
                    <MenuItem key={body.id} value={body.id}>
                      {body.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Type Field */}
            {editField === 'type' && (
              <FormControl fullWidth>
                <InputLabel id="edit-type-label">Type Style</InputLabel>
                <Select
                  labelId="edit-type-label"
                  value={editValue || ''}
                  onChange={(e: SelectChangeEvent) => setEditValue(e.target.value)}
                  input={<OutlinedInput label="Type Style" />}
                  MenuProps={MenuProps}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {sortedTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Year Field */}
            {editField === 'year' && (
              <TextField
                fullWidth
                label="Year"
                value={editValue || ''}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value;
                  if (value === '' || /^\d+$/.test(value)) {
                    setEditValue(value);
                  }
                }}
                placeholder="e.g., 2020"
                inputProps={{ inputMode: 'numeric' }}
              />
            )}

            {/* VIN Field */}
            {editField === 'vin' && (
              <TextField
                fullWidth
                label="VIN"
                value={editValue || ''}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setEditValue(e.target.value.toUpperCase())}
                placeholder="Vehicle Identification Number"
                inputProps={{ maxLength: 17 }}
                helperText="Enter VIN to apply to all selected vehicles"
              />
            )}

            {/* Flags Field */}
            {editField === 'flags' && (
              <FormControl fullWidth>
                <InputLabel id="edit-flags-label">Flags</InputLabel>
                <Select<string[]>
                  labelId="edit-flags-label"
                  multiple
                  value={editValue || []}
                  onChange={(e) => setEditValue(e.target.value)}
                  input={<OutlinedInput label="Flags" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as string[]).map((flagId) => {
                        const flag = flags.find((f) => f.id === flagId);
                        return (
                          <Chip
                            key={flagId}
                            label={flag?.label || flagId}
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  )}
                  MenuProps={MenuProps}
                >
                  {sortedFlags.map((flag) => (
                    <MenuItem key={flag.id} value={flag.id}>
                      {flag.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>Select one or more flags</FormHelperText>
              </FormControl>
            )}

            {/* Dataset Field */}
            {editField === 'dataset' && (
              <FormControl fullWidth>
                <InputLabel id="edit-dataset-label">Dataset</InputLabel>
                <Select
                  labelId="edit-dataset-label"
                  value={editValue || ''}
                  onChange={(e: SelectChangeEvent) => setEditValue(e.target.value)}
                  input={<OutlinedInput label="Dataset" />}
                  MenuProps={MenuProps}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {sortedDatasets.map((dataset) => (
                    <MenuItem key={dataset.id} value={dataset.id}>
                      {dataset.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Color Checked Field */}
            {editField === 'colorChecked' && (
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editValue === true}
                      onChange={(e) => setEditValue(e.target.checked)}
                    />
                  }
                  label="Mark colors as verified"
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  When enabled, this marks that the color annotations have been verified by an annotator.
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={editingVehicle ? saveEdit : saveBulkEdit}
            disabled={saving || (editField === 'make' && editValue && !editModelValue)}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <EditIcon />}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title={`Delete ${selectedVehicles.length} Vehicle(s)`}
        message="Are you sure you want to delete the selected vehicle(s)? This action cannot be undone."
        loading={deleting}
      />

      {/* Single Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteTargetId !== null}
        onClose={() => setDeleteTargetId(null)}
        onConfirm={handleConfirmDeleteSingle}
        title="Delete Vehicle"
        message="Are you sure you want to delete this vehicle? This action cannot be undone."
        loading={deleting}
      />

      {/* Remove Box Confirmation Dialog */}
      <ConfirmDialog
        open={removeBoxDialogOpen}
        onClose={() => setRemoveBoxDialogOpen(false)}
        onConfirm={handleConfirmRemoveBox}
        title="Remove Box"
        message={`Remove bounding box from ${selectedVehicles.length} selected vehicle(s)?`}
        confirmLabel="Remove"
        loading={removingBox}
      />

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default MMCSearch;
